/**
 * Ossature commune des écrans de contenu : une liste à gauche, une fiche à
 * droite, et le même pied de formulaire partout.
 *
 * Deux points ne sont pas décoratifs :
 *
 * - **Quitter une fiche modifiée demande confirmation.** Ces écrans servent à
 *   rédiger ; un clic distrait dans la liste ne doit pas emporter vingt minutes
 *   de texte.
 * - **Les erreurs de l'API se posent sous le bon champ.** `ErreurApi` les rend
 *   champ par champ (`{ erreurs: { slug: "…" } }`) : les fondre dans un bandeau
 *   générique obligerait à deviner lequel des douze champs est en cause.
 */
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { useState } from "react";
import type { ErreurApi } from "../api/client";

/** `"nouveau"` désigne une fiche en cours de création, `null` aucune sélection. */
export type Selection = number | "nouveau" | null;

export type EntreeListe = {
  id: number;
  libelle: string;
  secondaire?: string;
  publie?: boolean;
  /** Signale une page en défaut de qualité éditoriale. */
  enDefaut?: boolean;
};

type ProprietesAtelier = {
  intro: string;
  /** Bandeaux affichés au-dessus des deux colonnes (résumé de qualité…). */
  entete?: ReactNode;
  entrees: EntreeListe[];
  selection: Selection;
  surSelection: (selection: Selection) => void;
  libelleNouveau: string;
  /** Modifications non enregistrées : conditionne la demande de confirmation. */
  modifie: boolean;
  chargement: boolean;
  erreur: ErreurApi | null;
  /** Message affiché à la place de la fiche quand rien n'est sélectionné. */
  invite: string;
  children: ReactNode;
};

export function ContenuAtelier({
  intro,
  entete,
  entrees,
  selection,
  surSelection,
  libelleNouveau,
  modifie,
  chargement,
  erreur,
  invite,
  children,
}: ProprietesAtelier) {
  const [demande, setDemande] = useState<Selection | undefined>(undefined);

  function demanderSelection(cible: Selection) {
    if (cible === selection) return;
    if (modifie) {
      setDemande(cible);
      return;
    }
    surSelection(cible);
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        {intro}
      </Typography>

      {entete}

      {erreur && (
        <Alert severity="error" role="alert">
          {erreur.messageGlobal}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 3,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(260px, 340px) 1fr" },
        }}
      >
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => demanderSelection("nouveau")}
            >
              {libelleNouveau}
            </Button>
          </Box>

          <Divider />

          {chargement ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={40} />
            </Box>
          ) : entrees.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Rien à afficher pour l'instant.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: { lg: "70vh" }, overflowY: "auto", py: 0 }}>
              {entrees.map((entree) => (
                <ListItem key={entree.id} disablePadding>
                  <ListItemButton
                    selected={selection === entree.id}
                    aria-current={selection === entree.id ? "true" : undefined}
                    onClick={() => demanderSelection(entree.id)}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <Box component="span" sx={{ flexGrow: 1 }}>
                            {entree.libelle}
                          </Box>
                          {entree.enDefaut && (
                            <Tooltip title="Qualité éditoriale en défaut">
                              <ErrorOutlinedIcon fontSize="small" color="error" />
                            </Tooltip>
                          )}
                          {entree.publie === false && (
                            <Chip size="small" label="Brouillon" variant="outlined" />
                          )}
                        </Stack>
                      }
                      secondary={entree.secondaire}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        <Box>
          {selection === null ? (
            <Paper variant="outlined" sx={{ p: 4 }}>
              <Typography color="text.secondary">{invite}</Typography>
            </Paper>
          ) : (
            children
          )}
        </Box>
      </Box>

      <Dialog open={demande !== undefined} onClose={() => setDemande(undefined)}>
        <DialogTitle>Abandonner les modifications ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette fiche contient des modifications qui n'ont pas été enregistrées. Changer de fiche
            maintenant les perdrait définitivement.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDemande(undefined)}>Rester sur la fiche</Button>
          <Button
            color="error"
            onClick={() => {
              if (demande !== undefined) surSelection(demande);
              setDemande(undefined);
            }}
          >
            Abandonner
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Pied de formulaire
// ---------------------------------------------------------------------------

type ProprietesPied = {
  modifie: boolean;
  enregistrement: boolean;
  surEnregistrer: () => void;
  /** Absent pour une fiche jamais enregistrée : il n'y a rien à supprimer. */
  surSupprimer?: () => void;
  suppression?: boolean;
  /** Ce que la suppression fait disparaître, au singulier : « cette prestation ». */
  quoi: string;
  erreur: ErreurApi | null;
  succes: boolean;
};

export function PiedFormulaire({
  modifie,
  enregistrement,
  surEnregistrer,
  surSupprimer,
  suppression = false,
  quoi,
  erreur,
  succes,
}: ProprietesPied) {
  const [confirmation, setConfirmation] = useState(false);

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      {/* Une erreur qui vise un champ est déjà affichée sous lui : la recopier
          ici noierait le message utile dans un doublon. */}
      {erreur && (
        <Alert severity="error" role="alert">
          {erreur.erreurs._global ??
            (Object.keys(erreur.erreurs).length > 0
              ? "Certains champs ont été refusés ; le détail figure sous les champs concernés."
              : erreur.message)}
        </Alert>
      )}

      {succes && !modifie && (
        <Alert severity="success" aria-live="polite">
          Enregistré. Les modifications apparaîtront en ligne à la prochaine publication du site.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
        <Button
          variant="contained"
          startIcon={enregistrement ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={!modifie || enregistrement}
          onClick={surEnregistrer}
        >
          {enregistrement ? "Enregistrement…" : "Enregistrer"}
        </Button>

        {surSupprimer && (
          <Button
            color="error"
            startIcon={<DeleteOutlinedIcon />}
            disabled={suppression}
            onClick={() => setConfirmation(true)}
          >
            Supprimer
          </Button>
        )}

        {modifie && (
          <Typography variant="body2" color="warning.main">
            Modifications non enregistrées.
          </Typography>
        )}
      </Stack>

      <Dialog open={confirmation} onClose={() => setConfirmation(false)}>
        <DialogTitle>Supprimer {quoi} ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            La suppression est définitive. Si un autre contenu s'y rattache, l'API la refusera et
            dira lequel — rien ne sera supprimé en cascade.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(false)}>Annuler</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmation(false);
              surSupprimer?.();
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Petits utilitaires de formulaire
// ---------------------------------------------------------------------------

/**
 * État d'une fiche en cours d'édition : ce qui est affiché, ce qui est
 * enregistré, et l'écart entre les deux.
 *
 * Le brouillon n'est **jamais** réalimenté par un rafraîchissement de la liste.
 * React Query resonde volontiers en arrière-plan (retour sur l'onglet, cache
 * périmé) ; brancher le formulaire sur ces données effacerait le paragraphe en
 * cours de frappe. Le brouillon ne change donc qu'à trois moments explicites :
 * ouverture d'une fiche, saisie, et retour d'un enregistrement réussi.
 */
export function useFiche<T extends Record<string, unknown>>(vide: T) {
  const [selection, setSelection] = useState<Selection>(null);
  const [reference, setReference] = useState<T>(vide);
  const [brouillon, setBrouillon] = useState<T>(vide);
  const [succes, setSucces] = useState(false);

  return {
    selection,
    reference,
    brouillon,
    succes,
    modifie: JSON.stringify(brouillon) !== JSON.stringify(reference),

    /** Affiche une fiche, existante ou vierge. */
    ouvrir(cible: Selection, valeurs: T) {
      setSelection(cible);
      setReference(valeurs);
      setBrouillon(valeurs);
      setSucces(false);
    },

    modifier(champs: Partial<T>) {
      setBrouillon((actuel) => ({ ...actuel, ...champs }));
      setSucces(false);
    },

    /** Après enregistrement : la réponse de l'API devient la nouvelle référence. */
    confirmer(id: number, valeurs: T) {
      setSelection(id);
      setReference(valeurs);
      setBrouillon(valeurs);
      setSucces(true);
    },

    fermer() {
      setSelection(null);
      setReference(vide);
      setBrouillon(vide);
      setSucces(false);
    },
  };
}

/**
 * Champs réellement modifiés, pour un `PATCH` qui n'écrit que ce qui a bougé.
 * Renvoyer la fiche entière ferait remonter `updated_at` sur une simple
 * consultation, et écraserait ce qu'un autre onglet vient de corriger.
 */
export function champsModifies<T extends Record<string, unknown>>(
  brouillon: T,
  reference: T,
): Record<string, unknown> {
  const differences: Record<string, unknown> = {};
  for (const [cle, valeur] of Object.entries(brouillon)) {
    if (JSON.stringify(valeur) !== JSON.stringify(reference[cle])) differences[cle] = valeur;
  }
  return differences;
}

/** Message d'erreur de l'API pour un champ, ou `undefined` s'il est accepté. */
export function erreurChamp(erreur: ErreurApi | null, champ: string): string | undefined {
  return erreur?.pourChamp(champ);
}

/**
 * Slug proposé à partir d'un intitulé : minuscules, accents retirés, tirets
 * simples. Proposé seulement — jamais imposé sur une fiche déjà enregistrée,
 * car changer un slug change l'URL publique et casse les liens existants.
 */
export function slugifier(texte: string): string {
  return (
    texte
      .normalize("NFD")
      // Les diacritiques isolés par la décomposition NFD : « é » devient « e » + U+0301.
      .replace(/\p{M}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  );
}

/** Encadré titré, pour séparer les blocs d'un formulaire long. */
export function Section({ titre, children }: { titre: string; children: ReactNode }) {
  return (
    <Box component="section">
      <Typography variant="h4" component="h3" gutterBottom>
        {titre}
      </Typography>
      <Stack spacing={2.5}>{children}</Stack>
    </Box>
  );
}
